/**
 * /tracking/js-pixel — operator page for feature #162.
 *
 * Declares a TanStack Router route and renders the `JsPixelPage` feature.
 * Root wire-up picks this up from `router.tsx` when the feature lands on
 * prod-main.
 *
 * `?snippetId=` search param deep-links the page so operators can jump to
 * a specific snippet's event view straight from a notification or a link
 * shared in Slack.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { JsPixelPage } from "@/features/js-pixel/JsPixelPage";

interface TrackingJsPixelSearch {
	snippetId?: string;
}

export const trackingJsPixelRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/js-pixel",
	validateSearch: (
		raw: Record<string, unknown>,
	): TrackingJsPixelSearch => {
		const v = raw.snippetId;
		if (typeof v === "string" && v.trim().length > 0) {
			return { snippetId: v.trim() };
		}
		return {};
	},
	component: TrackingJsPixelRoute,
});

export default trackingJsPixelRoute;

export function TrackingJsPixelRoute() {
	const { snippetId } = trackingJsPixelRoute.useSearch();

	return (
		<div className="flex flex-col">
			<nav
				aria-label="Breadcrumb"
				className="mx-auto w-full max-w-5xl px-6 pt-10 text-sm text-zinc-500 sm:pt-16"
			>
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li className="text-zinc-900">JavaScript pixel</li>
				</ol>
			</nav>

			<JsPixelPage initialSnippetId={snippetId} />
		</div>
	);
}

// Named export for the root wire-up cycle.
export { TrackingJsPixelRoute as TrackingJsPixelPage };
